import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useRouter } from "next/router";
import { Toaster } from "react-hot-toast";
import { Ingredient } from "@/components/Recipe/Ingredient";
import { Instruction } from "@/components/Recipe/Instruction";
import Accordion from "@/components/Accordion/Accordion";
import CookingDone from "@/components/Home/CookingDoneScreen";
import { useIntercom } from "@/contexts/intercom/IntercomProvider";
import Loader from "@/components/Loaders/Loader";
import { toast } from "react-hot-toast";
import Link from "next/link";
import Head from "next/head";
import NavBar from "@/components/NavBar/NavBar";
import FlagIcon from "@/ui/Icons/FlagIcon";
import ExportIcon from "@/ui/Icons/ExportIcon";
import Hat from "@/ui/Icons/Hat";
import BowlIcon from "@/ui/Icons/BowlIcon";
import CalenderSolid from "@/ui/Icons/CalenderSolid";
import RoundedArrowBack from "@/ui/Icons/RoundedArrowBack";
import { useSupabaseClient, useUser } from "@supabase/auth-helpers-react";
import { slugToIdAndName } from "@/lib/slug";
// import useOnScreen from "@/hooks/useOnScreen";

export default function recipe({ recipe, host, profile }: any) {
  const ingredientsRef = useRef<HTMLDivElement>(null!);
  const instructionsRef = useRef<HTMLDivElement>(null!);
  const reviewsRef = useRef<HTMLDivElement>(null!);
  const [activeSection, setActiveSection] = useState("ingredients");
  const [doneCooking, setDoneCooking] = useState(false);
  // const isInstructionsVisible = useOnScreen(instructionsRef);
  const [isShared, setShared] = useState(false);
  const { show } = useIntercom();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [userCalendar, setUserCalendar] = useState<any>([]);
  const supabaseClient = useSupabaseClient();
  const user = useUser();

  const replace = async (oldRecipeId: any, calendarId: any) => {
    const newRecipeId = recipe.id;
    const { data, error } = await supabaseClient
      .from("calendar")
      .update({
        old_recipe_id: oldRecipeId,
        recipe_id: newRecipeId,
        skipped: false,
      })
      .eq("id", calendarId);
    if (error) {
      console.log(error);
    } else {
      fetchCalendar();
      toast.success("Recipe replaced successfully");
      // store recipe feedback
      await supabaseClient.from("recipe_feedback").insert([
        {
          recipe_id: oldRecipeId,
          swipe_rating: false,
          user_id: user?.id,
        },
        {
          recipe_id: newRecipeId,
          swipe_rating: true,
          user_id: user?.id,
        },
      ]);
    }
  };

  const fetchCalendar = async () => {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const { data, error } = await supabaseClient
      .from("calendar")
      .select(
        `*, 
        recipe!calendar_recipe_id_fkey(
        *,
        servings:serving(
               *,
               ingredients:serving_ingredient(
                  *,
                  ingredient(
                    *                                                                                  
                  )
               )                                                                                                                                                         
            )

        )`
      )
      .gte("date", todayDate?.toISOString())
      .order("date", { ascending: true });
    if (error) console.log("error", error);
    else {
      setUserCalendar(data);
    }
  };

  useEffect(() => {
    if (user) {
      fetchCalendar();
    }
  }, [user]);

  const router = useRouter();

  const handleButtonClick = (section: string) => {
    setActiveSection(section);
    switch (section) {
      case "ingredients":
        ingredientsRef.current.scrollIntoView({ behavior: "smooth" });
        break;
      case "instructions":
        instructionsRef.current.scrollIntoView({ behavior: "smooth" });
        break;
      case "reviews":
        reviewsRef.current.scrollIntoView({ behavior: "smooth" });
        break;
      default:
        break;
    }
  };

  const share = async (title: string, link: string) => {
    setShared(true);

    const data = {
      title: title,
      text: `I found ${
        recipe?.name
      } on Dinnerfy and thought of sharing it with you.
Click the link below to check it out and add it your dinnerfy.
Link : ${host + router?.asPath}`,
    };
    try {
      if (!navigator.canShare(data)) {
        setShared(false);
        throw new Error("Can't share data.", data as any);
      }
      await navigator.share(data);
      setShared(false);
    } catch (err: any) {
      console.error(err.name, err.message);
      setShared(false);
    }
  };

  return (
    <section className="h-full flex flex-col max-w-xl mx-auto relative ">
      <Head>
        <title>Dinnerfy</title>
        <meta name="title" content={recipe?.name} />
        <meta name="description" content={recipe?.description} />
        <meta
          property="og:image"
          content={`https://${host}/api/og?title=${
            recipe?.name
          }&tags=${encodeURIComponent(JSON.stringify(recipe?.dietTags))}&link=${
            recipe?.imageURL
          }`}
        />
        <meta property="og:title" content={recipe?.name} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={host} />
        <meta property="og:type" content="website" />
        <meta property="og:description" content={recipe?.description} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content={host} />
        <meta property="twitter:url" content={host} />
        <meta name="twitter:title" content={recipe?.name} />
        <meta name="twitter:description" content={recipe?.description} />
        <meta name="twitter:image" content={recipe?.imageURL} />
      </Head>
      {!doneCooking ? (
        <>
          <NavBar
            heading=""
            component={
              <div className="flex items-center gap-2">
                <div onClick={show}>
                  <FlagIcon />
                </div>
                {isShared ? (
                  <Loader size={5} />
                ) : (
                  <div onClick={() => share(recipe?.name, recipe?.imageURL)}>
                    <ExportIcon />
                  </div>
                )}
              </div>
            }
            wrapperClass="bg-[#F5E9ED]"
          />
          <div className="flex flex-col gap-5 px-5 justify-start items-center w-full bg-[#F5E9ED] py-5 rounded-b-2xl ">
            <p className="font-semibold text-xl">{recipe.name}</p>
            <img
              src={recipe.imageURL}
              className="w-52 h-52 rounded-full bg-white"
            />
            <div className="flex items-center gap-3 w-full">
              <div className="h-10 w-10 rounded-full bg-white p-1">
                <img
                  className="rounded-full"
                  src={profile?.avatar_url || "/avatar2.png"}
                />
              </div>
              <div className="flex-grow">
                <p className="font-semibold">
                  {profile ? profile.full_name : "By Dinnerfy"}
                </p>
              </div>
              <button
                onClick={() =>
                  user ? setShowDatePicker(true) : router.push("/start")
                }
              >
                <CalenderSolid />
              </button>
            </div>
          </div>
          <div className="flex flex-col px-6 py-4 gap-8">
            <p>{recipe.description}</p>
            {user ? null : (
              <div className="bg-[#E3F5EA] border border-[#15BC6E] rounded-xl p-3 flex flex-col gap-2">
                <p className="font-semibold text-lg">
                  Signup to Dinnerfy to explore more
                </p>
                <Link href="/start">
                  <div className="bg-[#15BC6E] rounded-lg py-2 px-3 font-medium text-white text-center">
                    Sign up
                  </div>
                </Link>
              </div>
            )}
            <div className="flex gap-4 flex-wrap">
              {recipe.dietTags.map((item: any) => (
                <div className="bg-white rounded-xl text-[#434343] py-2 px-5">
                  {item}
                </div>
              ))}
              {recipe.interestTags.tags.map((item: any) => (
                <div className="bg-white rounded-xl text-[#434343] py-2 px-5">
                  {item}
                </div>
              ))}
            </div>
            <div className="flex w-full justify-evenly">
              <div className="flex flex-col items-center gap-1">
                <Hat size={25} />
                <p>{recipe.prepTime} mins</p>
              </div>
              <div className="border-r border-gray-300"></div>
              <div className="flex flex-col items-center gap-1">
                <BowlIcon size={25} />
                <p>{recipe.cookTime} mins</p>
              </div>
            </div>
            <div className="flex flex-col gap-3 justify-start">
              <p className="font-semibold">Nutritional Value</p>
              <div className="flex w-full flex-nowrap gap-2 overflow-x-scroll">
                {recipe.nutritionFact.map((item: any) => (
                  <div className="bg-white rounded-xl text-[#434343] py-2 px-5 text-center font-medium min-w-fit">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/*  Grow container */}
          <div className="grow px-6">
            <p className="font-semibold py-3">Ingredients</p>
            <div
              ref={ingredientsRef}
              className="flex items-center justify-between scroll-m-52"
            >
              <span className="text-base text-[#434343] font-semibold">
                Total: <span>{recipe?.ingredients_meta.length}</span>
              </span>
              <span className="text-base text-[#434343] font-semibold">
                Serves: {recipe?.servingSize}
              </span>
            </div>

            {/* Ingredients TabPanel */}
            <div className="w-full bg-white p-6 rounded-3xl mt-4">
              {recipe?.ingredients_meta?.map((ingredient: any, key: string) => (
                <Ingredient ingredient={ingredient} key={key} />
              ))}
            </div>

            <hr className="border-b-[0.5px] border-gray-300 my-4" />

            <p className="font-semibold py-3">Instructions</p>

            {/* Instructions TabPanel */}
            <div ref={instructionsRef} className="w-full flex flex-col gap-y-2">
              <Instructions recipe={recipe} doneCooking={doneCooking} />
            </div>
            <hr className="border-b-[0.5px] border-gray-300 my-4" />
            <div className="w-full flex flex-col gap-y-2 mb-24">
              {recipe?.userIngredients && (
                <Accordion
                  title="User Ingredient"
                  discription={recipe?.userIngredients}
                />
              )}
              {recipe?.userInstructions && (
                <Accordion
                  title="User Instructions"
                  discription={recipe?.userInstructions}
                />
              )}
            </div>
            <div className="flex rounded-t-xl items-center border bg-white overflow-hidden border-[#15BC6E] fixed bottom-0 left-1/2 -translate-x-1/2 z-20 w-screen max-w-[34rem]">
              <button
                onClick={() => handleButtonClick("ingredients")}
                className={`w-full py-3 font-semibold bg-white border-r`}
              >
                Ingredients
              </button>
              <button
                onClick={() => handleButtonClick("instructions")}
                className={`w-full py-3 rounded-l-xl font-semibold bg-white`}
              >
                Instructions
              </button>
            </div>
            {showDatePicker && (
              <GroceryListModel
                closeModal={() => setShowDatePicker(false)}
                userCalendar={userCalendar}
                replace={replace}
              />
            )}
            {/* <div className="flex w-full px-4 mb-28">
              <button
                onClick={() => setDoneCooking(true)}
                className={`flex w-full my-2 bg-[#15BC6E] text-lg font-semibold rounded-xl text-white items-center gap-x-4 justify-center py-2 border border-slate-200 duration-300 ease-out active:scale-95 hover:bg-[#15BC65] disabled:bg-[#ADACAC] disabled:cursor-not-allowed disabled:pointer-events-none active:bg-[#15BC65]`}
              >
                Done Cooking
              </button>
            </div> */}
            {/*Review hidden*/}
            {/*<div ref={reviewsRef} className="px-4 flex flex-col gap-y-3">
                            {recipe?.reviews?.length > 0 && false ? (
                                recipe?.reviews?.map((review: any, key: string) => {
                                    return <Review key={key} item={review}/>;
                                })
                            ) : (
                                <>
                                    <div className="p-10 grid place-items-center">
                                        <h3 className="font-semibold text-lg">
                                            Opps! no reviews for this recipe
                                        </h3>
                                        <span>be the first to review</span>
                                    </div>
                                </>
                            )}
                        </div>*/}
          </div>
        </>
      ) : (
        <CookingDone />
      )}
      <Toaster
        toastOptions={{
          success: {
            duration: 3000,
            style: {
              background: "#15BC6E",
              color: "white",
            },
          },
        }}
      />
    </section>
  );
}

const GroceryListModel: FC<any> = ({ closeModal, userCalendar, replace }) => {
  return (
    <div className="fixed left-0 top-0 z-[1055] h-full overflow-y-auto overflow-x-hidden outline-none bg-transparent w-full flex justify-center items-center">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm "
        onClick={closeModal}
      />
      <div className="pointer-events-none flex relative transition-all duration-300 max-w-md px-4 ease-in-out min-h-[calc(100%-1rem)] w-full items-center">
        <div className="min-[576px]:shadow-[0_0.5rem_1rem_rgba(#000, 0.15)] pointer-events-auto relative flex w-full flex-col border-none rounded-[18px] overflow-hidden bg-white bg-clip-padding text-current shadow-lg outline-none">
          <div className="z-10 w-full min-w flex flex-col">
            <div className="flex items-center py-3 px-5 bg-[#15BC6E]">
              <button onClick={() => closeModal()}>
                <RoundedArrowBack />
              </button>
              <p className="text-lg text-center w-full text-white">
                Select a recipe to replace
              </p>
            </div>
            <div className="py-3 px-5 max-h-96 overflow-y-scroll">
              {userCalendar.map((item: any, key: string) => (
                <div key={key}>
                  <IngredientCard
                    recipe={item}
                    date={item.date}
                    skipped={item.skipped}
                    replace={replace}
                    closeModal={closeModal}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const IngredientCard: FC<any> = ({
  recipe,
  date,
  skipped,
  replace,
  closeModal,
}) => {
  const ingredientDate = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(date));

  return (
    <div
      className={`flex py-3 px-4 mb-3 gap-3 border-[0.5px] border-[#E4E2E3] items-center rounded-2xl hover:scale-[1.01] duration-200 transition-transform ease-out hover:bg-slate-100`}
      onClick={() => {
        replace(recipe.recipe_id, recipe.id);
        closeModal();
      }}
    >
      <div className="text-xs whitespace-nowrap">{ingredientDate}</div>
      <div className="flex-grow font-medium text-ellipsis overflow-hidden whitespace-nowrap">
        {skipped ? "Night Off" : <>{recipe.recipe.name}</>}
      </div>
      {/* {!skipped && (
        <button
          className="mr-4">
            replace
          </button>
      )} */}
    </div>
  );
};

const Instructions: FC<any> = ({ recipe, doneCooking }) => {
  const [doneItems, setDoneItems] = useState<any[]>([]);
  return (
    <>
      {recipe?.instructions?.map((instruction: any, key: string) => (
        <Instruction key={key} item={instruction} setDoneItems={setDoneItems} />
      ))}
    </>
  );
};

export async function getServerSideProps({ params, req }: any) {
  let host = "";
  if (req) {
    host = req.headers.host;
  }
  const { data, error } = await supabase
    .from("recipe")
    .select(
      `*,servings:serving(*,
        ingredients:serving_ingredient(
          *,ingredient(*)
        )
        ),
        reviews:recipe_feedback(
         id,
         recipe_id,
         text,
         created_at,
         star_rating,
         user:profiles(
           id,
           full_name,
                  avatar_url,
                  email
                )
        )`
    )
    .eq("id", slugToIdAndName(params?.recipe).id)
    .limit(1)
    .single();
  if (error) console.log("Failed to fetch recipes", error);

  let profile = null;

  if (data?.creatorID !== "") {
    const { data: profile_, error: profileError } = await supabase
      .from("profiles")
      .select(`*`)
      .eq("email", data?.creatorID)
      .limit(1)
      .single();

    if (profileError) console.log("Failed to creator profile", profileError);
    profile = profile_;
  }

  return {
    props: {
      recipe: data,
      host,
      profile,
    },
  };
}
